import { Box } from '@mui/material';
import { useState } from 'react';

import {
  useBoardEditingStore,
  useBoardSelectionStore,
  useWallContentStore,
  type WallBoard,
  type WallCard,
} from '@walls/store';
import { useCardCommands } from '../cards/actions/useCardCommands';
import { useCardPermissions } from '../cards/hooks/useCardPermissions';
import { isImageCard } from '../cards/utils/cardDisplay';
import { useClusterCommands } from '../clusters/hooks/useClusterCommands';
import { useConnectionCommands } from '../connections/useConnectionCommands';
import { InlineColorMenu } from './InlineColorMenu';
import { InlineMenuButton } from './InlineMenuButton';
import { inlineMenuSx } from './inlineMenuStyles';
import { stopMenuEvent, stopPointerEvent } from './menuEvents';

interface CardOrClusterInlineMenuProps {
  board: WallBoard;
  card: WallCard;
  isCluster: boolean;
  left: number;
  top: number;
}

export function CardOrClusterInlineMenu({
  board,
  card,
  isCluster,
  left,
  top,
}: CardOrClusterInlineMenuProps) {
  const [colorsOpen, setColorsOpen] = useState(false);
  const connections = useWallContentStore((state) => state.connections);
  const setConnectingFromId = useBoardSelectionStore((state) => state.setConnectingFromId);
  const startEditCard = useBoardEditingStore((state) => state.startEditCard);
  const startEditImage = useBoardEditingStore((state) => state.startEditImage);
  const cardCommands = useCardCommands(board);
  const clusterCommands = useClusterCommands(board);
  const { deleteConnectionsByCardId } = useConnectionCommands(board);
  const permissions = useCardPermissions(board, card);
  const isClusterChild = Boolean(card.parentId);
  const isResized = card.width != null || card.height != null;
  const canUseUnlockedActions = !card.isLocked;
  const hasConnections = connections.some(
    (connection) => connection.start?.cardId === card.id || connection.end?.cardId === card.id
  );

  const edit = () => {
    if (isImageCard(card)) {
      startEditImage(card.id);
    } else {
      startEditCard(card.id);
    }
  };

  const setColor = (color: string) => {
    setColorsOpen(false);
    void cardCommands.setCardColor(card.id, color);
  };

  return (
    <Box
      className="inline-menu"
      onClick={stopMenuEvent}
      onDoubleClick={stopMenuEvent}
      onPointerDown={stopPointerEvent}
      sx={{
        ...inlineMenuSx,
        left,
        top,
      }}
    >
      {canUseUnlockedActions ? (
        <>
          {isCluster && (
            <InlineMenuButton
              label={card.collapsed ? 'Expand' : 'Collapse'}
              separatedEnd={true}
              onClick={() => void clusterCommands.toggleClusterCollapsed(card)}
            >
              {card.collapsed ? (
                <svg height="20" viewBox="0 0 24 24">
                  <path d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z"></path>
                </svg>
              ) : (
                <svg height="20" viewBox="0 0 24 24">
                  <path d="m12 8-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z"></path>
                </svg>
              )}
            </InlineMenuButton>
          )}

          <Box sx={{ position: 'relative' }}>
            <InlineMenuButton
              label="Color"
              separatedEnd={!permissions.canEditContent}
              onClick={() => setColorsOpen((open) => !open)}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  border: '1px solid #dddddd',
                  bgcolor: card.color,
                  borderRadius: '50%',
                }}
              />
            </InlineMenuButton>
            {colorsOpen && <InlineColorMenu currentColor={card.color} onSelect={setColor} />}
          </Box>

          {permissions.canEditContent && (
            <InlineMenuButton label="Edit" separatedEnd={true} onClick={edit}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20"
                viewBox="0 0 22 22"
                style={{ fill: 'none', stroke: '#000000', strokeWidth: 2 }}
              >
                <path
                  d="M21.827,4.553,23.276,6a2.473,2.473,0,0,1,0,3.5L8.947,23.828H4V18.881L18.329,4.553A2.473,2.473,0,0,1,21.827,4.553ZM16.367,6.514l4.947,4.947"
                  transform="translate(-3 -2.828)"
                ></path>
              </svg>
            </InlineMenuButton>
          )}

          {!isCluster && (
            <InlineMenuButton
              label="Duplicate"
              onClick={() => void cardCommands.duplicateCard(card)}
            >
              <svg height="20" viewBox="0 0 24 24">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2m0 16H8V7h11z"></path>
              </svg>
            </InlineMenuButton>
          )}

          {board.allowCardResizing && isResized && (
            <InlineMenuButton
              label="Undo resizing"
              onClick={() =>
                isCluster
                  ? void clusterCommands.resetClusterSize(card.id)
                  : void cardCommands.resetCardSize(card.id)
              }
            >
              <svg height="22" viewBox="0 0 24 24">
                <path d="M22 3.41 16.71 8.7 20 12h-8V4l3.29 3.29L20.59 2zM3.41 22l5.29-5.29L12 20v-8H4l3.29 3.29L2 20.59z"></path>
              </svg>
            </InlineMenuButton>
          )}

          {!isClusterChild && (
            <InlineMenuButton label="Connect" onClick={() => setConnectingFromId(card.id)}>
              <svg height="24" viewBox="0 0 24 24">
                <path d="M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3z"></path>
              </svg>
            </InlineMenuButton>
          )}

          {!isClusterChild && hasConnections && (
            <InlineMenuButton
              label="Clear connections"
              onClick={() => void deleteConnectionsByCardId(card.id)}
            >
              <svg height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3z"></path>
                <line x1="0" y1="0" x2="24" y2="24" stroke="black" strokeWidth="2"></line>
                <line x1="3" y1="0" x2="27" y2="24" stroke="white" strokeWidth="2"></line>
              </svg>
            </InlineMenuButton>
          )}

          {!isClusterChild && board.allowCardLocking && permissions.canLock && (
            <InlineMenuButton
              label="Lock"
              onClick={() =>
                isCluster
                  ? void clusterCommands.lockCluster(card.id)
                  : void cardCommands.lockCard(card.id)
              }
            >
              <svg height="20" viewBox="0 0 24 24">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2m-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2m3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1z"></path>
              </svg>
            </InlineMenuButton>
          )}

          {permissions.canEditContent && (
            <InlineMenuButton
              label="Delete"
              separatedStart={true}
              onClick={() =>
                isCluster
                  ? void clusterCommands.deleteCluster(card.id)
                  : void cardCommands.deleteCard(card.id)
              }
            >
              <svg
                height="20"
                viewBox="0 0 20 22"
                style={{ fill: 'none', stroke: '#000000', strokeWidth: 2 }}
              >
                <g transform="translate(-2 -1)">
                  <path d="M3,6H21"></path>
                  <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
                </g>
              </svg>
            </InlineMenuButton>
          )}
        </>
      ) : (
        board.allowCardUnlocking &&
        permissions.canUnlock && (
          <InlineMenuButton
            label="Unlock"
            onClick={() =>
              isCluster
                ? void clusterCommands.unlockCluster(card.id)
                : void cardCommands.unlockCard(card.id)
            }
          >
            <svg height="20" viewBox="0 0 24 24">
              <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2m6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2m0 12H6V10h12z"></path>
            </svg>
          </InlineMenuButton>
        )
      )}
    </Box>
  );
}
